<template>
    <v-container class="px-5">
        <v-btn variant="text" size="small" prepend-icon="mdi-arrow-left" class="mb-2"
               :to="{ name: 'admin-ctc-submissions' }">Back to submissions</v-btn>

        <v-skeleton-loader v-if="loading" type="card, table"/>

        <template v-else-if="sub">
            <v-card rounded="lg" variant="outlined" class="mb-4">
                <v-card-text>
                    <v-row align="center">
                        <v-col>
                            <div class="text-subtitle-1 font-weight-bold">
                                {{ sub.company || 'Unknown company' }} - {{ sub.asset_name || ('Asset ' + sub.asset_id) }}
                            </div>
                            <div class="text-caption text-medium-emphasis">
                                Device <code>{{ sub.device_serial || '-' }}</code> · Record #{{ sub.record_id ?? '-' }}
                                · Collected {{ sub.collected_at ? formatDateTime(sub.collected_at) : '-' }}
                            </div>
                        </v-col>
                        <v-col cols="auto" class="d-flex ga-2 align-center">
                            <v-chip size="small" variant="tonal" :color="outcomeColor">{{ outcomeLabel }}</v-chip>
                            <v-chip size="small" variant="tonal" :color="transportColor">{{ sub.transport_status || '-' }}</v-chip>
                            <v-btn v-if="sub.byte_size" color="primary" variant="tonal" size="small"
                                   prepend-icon="mdi-download" :loading="downloading" @click="downloadPackage">Download</v-btn>
                            <v-btn color="primary" variant="tonal" size="small" prepend-icon="mdi-refresh"
                                   :loading="resubmitting" @click="confirmResubmit = true">Resubmit</v-btn>
                        </v-col>
                    </v-row>
                    <v-alert v-if="sub.error_message" type="error" variant="tonal" density="compact" class="mt-3">
                        {{ sub.error_message }}
                    </v-alert>
                </v-card-text>
            </v-card>

            <v-tabs v-model="tab" class="mb-3">
                <v-tab value="summary">Summary</v-tab>
                <v-tab value="frames">Raw frames<v-chip size="x-small" class="ml-2">{{ frames.length }}</v-chip></v-tab>
                <v-tab value="decoded">Decoded</v-tab>
                <v-tab value="events">Events<v-chip size="x-small" class="ml-2">{{ events.length }}</v-chip></v-tab>
            </v-tabs>

            <v-window v-model="tab">
                <!-- Summary -->
                <v-window-item value="summary">
                    <v-card rounded="lg" variant="outlined">
                        <v-card-text>
                            <div v-for="f in summaryFields" :key="f.label"
                                 class="d-flex justify-space-between py-2 border-b">
                                <span class="text-medium-emphasis">{{ f.label }}</span>
                                <span class="font-weight-medium text-right">
                                    {{ f.value }}
                                    <v-btn v-if="f.copy" icon size="x-small" variant="text" @click="copyToClipboard(f.value)">
                                        <v-icon size="13">mdi-content-copy</v-icon>
                                    </v-btn>
                                </span>
                            </div>
                        </v-card-text>
                    </v-card>
                </v-window-item>

                <!-- Raw frames -->
                <v-window-item value="frames">
                    <v-card rounded="lg" variant="outlined">
                        <v-card-text>
                            <div v-if="!frames.length" class="text-center py-6 text-medium-emphasis">
                                No frames in this capture (non-data outcome).
                            </div>
                            <v-table v-else density="compact" class="frames">
                                <thead>
                                    <tr><th>Time</th><th>Dir</th><th>CAN ID</th><th>Data</th></tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(fr, i) in frames" :key="i">
                                        <td class="text-caption text-no-wrap">{{ fr.timestamp }}</td>
                                        <td><v-chip size="x-small" variant="tonal" :color="fr.type === 'REQ' ? 'info' : 'grey'">{{ fr.type }}</v-chip></td>
                                        <td><code>{{ fr.can_id }}</code></td>
                                        <td><code>{{ fr.data }}</code></td>
                                    </tr>
                                </tbody>
                            </v-table>
                        </v-card-text>
                    </v-card>
                </v-window-item>

                <!-- Decoded -->
                <v-window-item value="decoded">
                    <div v-if="!ecus.length" class="text-center py-6 text-medium-emphasis">No decoded ECU data.</div>
                    <v-card v-for="(ecu, ei) in ecus" :key="ei" rounded="lg" variant="outlined" class="mb-3">
                        <v-card-title class="text-subtitle-2">
                            {{ ecu.name }} <span class="text-caption text-medium-emphasis">@ {{ ecu.address }}</span>
                            <v-chip size="x-small" class="ml-2" variant="tonal" :color="ecu.mil === 'on' ? 'error' : 'success'">
                                MIL {{ ecu.mil }}
                            </v-chip>
                        </v-card-title>
                        <v-expansion-panels variant="accordion" multiple>
                            <v-expansion-panel v-for="(g, gi) in ecu.groups" :key="gi" :title="g.comment || g.semantic">
                                <v-expansion-panel-text>
                                    <v-table v-if="g.measurements && g.measurements.length" density="compact">
                                        <tbody>
                                            <tr v-for="(m, mi) in g.measurements" :key="mi">
                                                <td class="text-medium-emphasis" style="width: 90px">{{ m.name }}</td>
                                                <td class="font-weight-medium">{{ m.value }} {{ m.unit }}</td>
                                                <td class="text-caption">{{ m.comment }}</td>
                                            </tr>
                                        </tbody>
                                    </v-table>
                                    <div v-for="(pg, pi) in g.pgn_groups || []" :key="'p' + pi" class="mt-2">
                                        <div class="text-caption font-weight-medium">{{ pg.identifier }} - {{ pg.comment }}</div>
                                        <v-table density="compact">
                                            <tbody>
                                                <tr v-for="(m, mi) in flattenPgn(pg)" :key="mi">
                                                    <td class="text-medium-emphasis" style="width: 110px">{{ m.name }}</td>
                                                    <td>{{ m.value }} {{ m.unit }}</td>
                                                    <td class="text-caption">{{ m.comment }}</td>
                                                </tr>
                                            </tbody>
                                        </v-table>
                                    </div>
                                </v-expansion-panel-text>
                            </v-expansion-panel>
                        </v-expansion-panels>
                    </v-card>
                </v-window-item>

                <!-- Events -->
                <v-window-item value="events">
                    <v-card rounded="lg" variant="outlined">
                        <v-card-text>
                            <v-timeline v-if="events.length" density="compact" side="end" align="start">
                                <v-timeline-item v-for="(e, i) in events" :key="i" size="x-small"
                                                 :dot-color="eventColor(e.event)">
                                    <div class="d-flex justify-space-between ga-4">
                                        <div>
                                            <span class="font-weight-medium">{{ e.event }}</span>
                                            <div class="text-caption text-medium-emphasis">{{ e.detail }}</div>
                                        </div>
                                        <div class="text-caption text-no-wrap">{{ e.at ? formatDateTime(e.at) : '' }}</div>
                                    </div>
                                </v-timeline-item>
                            </v-timeline>
                            <div v-else class="text-center py-6 text-medium-emphasis">No events recorded.</div>
                        </v-card-text>
                    </v-card>
                </v-window-item>
            </v-window>
        </template>

        <v-dialog v-model="confirmResubmit" max-width="440">
            <v-card>
                <v-card-title>Reprocess submission</v-card-title>
                <v-card-text>This rebuilds the artifacts and re-attempts transmission to CTC-VIS. Continue?</v-card-text>
                <v-card-actions>
                    <v-spacer/>
                    <v-btn @click="confirmResubmit = false">Cancel</v-btn>
                    <v-btn color="primary" :loading="resubmitting" @click="resubmit">Resubmit</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script setup>
import {computed, onMounted, ref} from 'vue';
import {useRoute, useRouter} from 'vue-router';
import {send} from '@/composables/apiClient';
import {useSnackbarStore} from '@/stores/snackBarStore';
import {useAuthStore} from '@/stores/authStore';
import {useClipboard} from '@/composables/clipboard';
import {formatDateTime} from '@/composables/useTimezone';

const route = useRoute();
const router = useRouter();
const {showSnackbar} = useSnackbarStore();
const authStore = useAuthStore();
const {copyToClipboard} = useClipboard();

const loading = ref(true);
const resubmitting = ref(false);
const downloading = ref(false);
const confirmResubmit = ref(false);
const sub = ref(null);
const tab = ref('summary');

const frames = computed(() => sub.value?.device_capture?.frames ?? []);
const ecus = computed(() => sub.value?.decoded_capture?.ecus ?? []);
const events = computed(() => sub.value?.events ?? []);

const outcomeColor = computed(() => {
    if (!sub.value) return 'grey';
    if (sub.value.outcome !== 'data') return 'error';
    return {pass: 'success', fail: 'error'}[sub.value.result?.carb_status] || 'grey';
});
const outcomeLabel = computed(() => {
    if (!sub.value) return '';
    return sub.value.outcome === 'data' ? (sub.value.result?.carb_status || 'data') : sub.value.outcome.replace(/_/g, ' ');
});
const transportColor = computed(() =>
    ({sent: 'success', acked: 'success', failed: 'error', queued: 'grey'}[sub.value?.transport_status] || 'grey'));

const summaryFields = computed(() => {
    const s = sub.value;
    if (!s) return [];
    return [
        {label: 'Pipeline stage', value: s.pipeline_stage || '-'},
        {label: 'Submitted', value: s.submitted_at ? formatDateTime(s.submitted_at) : 'Not submitted'},
        {label: 'Retry count', value: String(s.retry_count ?? 0)},
        {label: 'File base', value: s.file_base || '-'},
        {label: 'Content hash', value: s.content_hash || '-', copy: !!s.content_hash},
        {label: 'Receipt hash', value: s.receipt_hash || '-', copy: !!s.receipt_hash},
        {label: 'Package size', value: s.byte_size ? `${s.byte_size} bytes` : '-'},
        {label: 'Raw capture size', value: s.device_capture_bytes ? `${s.device_capture_bytes} bytes` : '-'},
        {label: 'Raw capture sha256', value: s.device_capture_sha256 || '-', copy: !!s.device_capture_sha256},
    ];
});

const flattenPgn = (pg) => {
    const out = [...(pg.measurements || [])];
    (pg.sets || []).forEach((set) => (set.measurements || []).forEach((m) => out.push(m)));
    return out;
};

const eventColor = (event) => ({
    captured: 'info', packaged: 'info', sent: 'success', staged: 'grey',
    outcome_recorded: 'warning', failed: 'error', awaiting_encryption_key: 'warning',
    data_check_warning: 'warning',
}[event] || 'grey');

const load = () => {
    loading.value = true;
    send('get', `admin/ctc/submissions/${route.params.id}`)
        .then((res) => {
            sub.value = res.data.data;
        })
        .catch(() => showSnackbar('Submission could not be loaded', 'error'))
        .finally(() => loading.value = false);
};

const resubmit = () => {
    resubmitting.value = true;
    send('post', `admin/ctc/submissions/${route.params.id}/resubmit`)
        .then(() => {
            showSnackbar('Submission reprocessed');
            confirmResubmit.value = false;
            load();
        })
        .catch(() => showSnackbar('Resubmit failed', 'error'))
        .finally(() => resubmitting.value = false);
};

// Pull the stored CARB package file (the .gpg, or the staged .zip) so a super
// admin can hand CARB the exact submitted artifact on request. The endpoint
// streams the file and logs the access on the submission's event log.
const downloadPackage = () => {
    downloading.value = true;
    send('get', `admin/ctc/submissions/${route.params.id}/download`, null, {responseType: 'blob'})
        .then((res) => {
            const disposition = res.headers['content-disposition'] || '';
            const match = disposition.match(/filename="?([^";]+)"?/);
            const filename = match ? match[1] : `ctc_submission_${route.params.id}`;
            const blobUrl = window.URL.createObjectURL(res.data);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);
        })
        .catch(() => showSnackbar('Package file is not available to download', 'error'))
        .finally(() => downloading.value = false);
};

onMounted(() => {
    if (!authStore.user?.super) {
        showSnackbar('Super admin access required', 'error');
        router.replace({name: 'dashboard'});
        return;
    }
    load();
});
</script>

<style scoped>
code {
    font-family: monospace;
}

.frames code {
    font-size: 12px;
}
</style>
